import React, { useState, useRef, useMemo, useEffect, useContext } from 'react'
import {
  Switch,
  withRouter,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom'
import {
  AutoComplete,
  Breadcrumb,
  Input,
  Layout,
  Menu,
  Notification,
  Spin,
} from '@arco-design/web-react'
import cs from 'classnames'
import {
  IconMenuFold,
  IconMenuUnfold,
  IconSearch,
} from '@arco-design/web-react/icon'
import { useDispatch, useSelector } from 'react-redux'
import qs from 'query-string'
import NProgress from 'nprogress'
import Loadable from 'react-loadable'
import Navbar from './components/NavBar'
import Footer from './components/Footer'
import useRoute, { IRoute } from '@/routes'
import useLocale, { useI18n } from './hooks/useLocale'
import getUrlParams from './utils/getUrlParams'
import styles from './style/layout.module.less'
import {
  addCacheRoute,
  getAnchorPoint,
  getCacheRoute,
  getMenuCollapse,
  getSettings,
  getUserLoading,
  setMenuCollapse,
  trigger,
  watchEvent,
} from './store/slices/appSlice'
import NotLiveRoute from 'react-live-route'
import lazyload from './utils/lazyload'
import HeaderTab from './components/HeaderTab'
import { getConfiguration } from './store/slices/userSlice'
import MenuWithPopover from './components/MenuWithPopover'
import { GlobalContext } from './context'
import { Anchor } from '@arco-design/web-react'
import { parseQueryString } from './utils/tools'
import DynamicLiveRoute from './dynamicLiveRoute'
import {
  buildDynamicDetailCacheRoute,
  getActionTitle,
  getDynamicDetailBreadcrumb,
  matchDynamicDetailRoute,
  normalizeRouteKey,
} from './utils/dynamicDetailRoute'
const AnchorLink = Anchor.Link
const LiveRoute = withRouter(NotLiveRoute)
const MenuItem = Menu.Item
const SubMenu = Menu.SubMenu
const Sider = Layout.Sider
const Content = Layout.Content
const hiddenFirstBreadcrumbKeys = ['t_client']

function getBreadcrumbText(node, locale, lang) {
  if (typeof node === 'object' && node !== null) {
    return (!lang || lang === 'zh-CN') ? node.name : (node.nameEn || node.name)
  }
  return typeof node === 'string' ? locale[node] || node : undefined
}

function getDisplayBreadcrumb(breadcrumb, locale, lang) {
  return breadcrumb.reduce((items, node, index) => {
    const text = getBreadcrumbText(node, locale, lang)
    if (index === 0 && text && hiddenFirstBreadcrumbKeys.some(k => locale[k] === text)) {
      return items
    }

    const prevText = getBreadcrumbText(items[items.length - 1], locale, lang)
    if (text && prevText === text) {
      return items
    }

    items.push(node)
    return items
  }, [])
}

function getIconFromKey(route: IRoute) {
  if (!route.Icon) return <div className={styles['icon-empty']} />
  const Icon = route.Icon
  return <Icon className={styles.icon} />
}
function Loading() {
  const t = useI18n()
  return (
    <div className={styles.spin}>
      <Spin dot tip={t('t_loading')} />
    </div>
  )
}

function getFlattenRoutes(routes) {
  const res = []
  function travel(_routes) {
    _routes.forEach((route) => {
      const visibleChildren = (route.children || []).filter(
        (child) => !child.ignore
      )
      if (route.key && (!route.children || !visibleChildren.length)) {
        try {
          if (!route.dynamic) {
            route.component = Loadable({
              loader: () => import(`./pages/${route.path ?? route.key}`),
              loading: Loading,
            })
          }
          if (!route?.children) {
            res.push(route)
          }
        } catch (e) {
          console.error(e)
        }
      }
      if (route.children && route.children.length) {
        travel(route.children)
      }
    })
  }
  travel(routes)
  return res
}

function PageLayout() {
  const urlParams = getUrlParams()
  const history = useHistory()
  const pathname = history.location.pathname
  const currentComponent = qs.parseUrl(pathname).url.slice(1)
  const { lang } = useContext(GlobalContext)
  const locale = useLocale()
  const settings = useSelector(getSettings)
  const userLoading = useSelector(getUserLoading)
  const cacheRoute = useSelector(getCacheRoute)
  const anchorPoint = useSelector(getAnchorPoint)
  const configuration = useSelector(getConfiguration)
  const [routes, defaultRoute] = useRoute()
  const defaultSelectedKeys = [currentComponent || defaultRoute]
  const paths = (currentComponent || defaultRoute).split('/')
  const defaultOpenKeys = paths.slice(0, paths.length - 1)
  const [breadcrumb, setBreadCrumb] = useState([])
  const menuCollapsed = useSelector(getMenuCollapse)
  const dispatch = useDispatch()
  const [selectedKeys, setSelectedKeys] =
    useState<string[]>(defaultSelectedKeys)
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys)

  const routeMap = useRef<Map<string, React.ReactNode[]>>(new Map())
  const menuMap = useRef<
    Map<string, { menuItem?: boolean; subMenu?: boolean }>
  >(new Map())

  const navbarHeight = 60
  const menuWidth = menuCollapsed ? 48 : settings.menuWidth
  const popUpMenu = settings.popUpMenu
  const popUpMenuType = settings.popUpMenuType
  const showNavbar = settings.navbar && urlParams.navbar !== false
  const showMenu = settings.menu && urlParams.menu !== false
  const showFooter = settings.footer && urlParams.footer !== false
  const event = useSelector(watchEvent)

  const flattenRoutes = useMemo(() => getFlattenRoutes(routes) || [], [routes])
  const location = useLocation()
  const displayBreadcrumb = useMemo(
    () => getDisplayBreadcrumb(breadcrumb, locale, lang),
    [breadcrumb, locale, lang]
  )
  function getRouteTitle(r) {
    return !lang || lang == 'zh-CN' ? r.name : r.nameEn
  }
  const isDevRoute = useMemo(
    () => new URLSearchParams(location.search).get('isDev') === 'true',
    [location.search]
  )
  const devRouteComponent = useMemo(() => {
    const pathName = location.pathname
    const routeKey = pathName.replace(/^\/+/, '')
    if (!isDevRoute || !routeKey) return null
    if (flattenRoutes.find((route) => '/' + route.key === pathName)) return null

    return Loadable({
      loader: () => import(`./pages/${routeKey}`),
      loading: Loading,
    })
  }, [isDevRoute, location.pathname, flattenRoutes])
  function getMenuTitle(r) {
    const iconDom = getIconFromKey(r)
    const titleDom = (
      <>
        {/* {iconDom} {locale[r.name] || r.name} */}
        {iconDom} {getRouteTitle(r)}
      </>
    )
    return titleDom
  }
  const setRouteMap = (route, parentNode = []) => {
    const { breadcrumb = true } = route
    const title = { name: route.name, nameEn: route.nameEn || route.name }
    routeMap.current.set(
      `/${route.key}`,
      breadcrumb ? [...parentNode, title] : []
    )
  }
  function travel(_routes: IRoute[], level, parentNode = []) {
    const popupMenu = popUpMenu
    return _routes.map((route) => {
      const { ignore } = route
      const titleDom = getMenuTitle(route)
      setRouteMap(route, parentNode)
      const visibleChildren = (route.children || []).filter((child) => {
        const { ignore } = child
        if (ignore || route.ignore) {
          // setRouteMap(child, parentNode);
          const title = { name: route.name, nameEn: route.nameEn || route.name }
          setRouteMap(
            child,
            parentNode?.length ? [...parentNode, title] : [title]
          ) //新增手动设置parent 24-4-11[...parentNode, title]多传一层
        }
        return !ignore
      })
      if (
        route.children &&
        route.children?.every((child) => child.ignore === true)
      ) {
        return ''
      }
      if (ignore) {
        return ''
      }

      if (visibleChildren.length) {
        const title = { name: route.name, nameEn: route.nameEn || route.name }
        menuMap.current.set(route.key, { subMenu: true })
        return popupMenu ? (
          <MenuWithPopover
            type={popUpMenuType}
            setRouteMap={setRouteMap}
            parentNode={[...parentNode, title]}
            key={route.key}
            route={route}
            visibleChildren={visibleChildren}
            onClickMenuItem={(routekey) => {
              onClickMenuItem(routekey)
            }}
          />
        ) : (
          <SubMenu key={route.key} title={titleDom}>
            {travel(visibleChildren, level + 1, [...parentNode, title])}
          </SubMenu>
        )
      } else {
        // const roles = configuration?.currentUser?.roles;
        // if (roles && roles.find((c) => c == 'admin')) {
        menuMap.current.set(route.key, { menuItem: true })
        return <MenuItem key={route.key}>{titleDom}</MenuItem>
        // }
      }
    })
  }
  function renderRoutes(_routes: IRoute[], level) {
    routeMap.current.clear()
    menuMap.current.clear()

    const menuNodes = []
    _routes.forEach((route) => {
      const { ignore } = route
      const visibleChildren = (route.children || []).filter(
        (child) => !child.ignore
      )

      if (ignore) {
        setRouteMap(route)
        return
      }

      if (visibleChildren.length) {
        setRouteMap(route)
        const title = { name: route.name, nameEn: route.nameEn || route.name }
        menuNodes.push(...travel(visibleChildren, level + 1, [
          title,
        ]))
        return
      }

      menuNodes.push(...travel([route], level))
    })

    return menuNodes
  }

  function onClickMenuItem(routeKey, extra = undefined) {
    let key = routeKey
    let subTitle = undefined
    let customTitle = undefined //自定义title
    let urlParams = extra?.['urlParams'] ? '?' + extra?.['urlParams'] : ''
    if (flattenRoutes.length <= 1) {
      return
    }
    let currentRoute = flattenRoutes.find((r) => r.key === key)
    const detailPathRoute = !currentRoute
      ? flattenRoutes.find((r) => {
          const detailPath = normalizeRouteKey(r.detailPath || r.detailFormRoute)
          return detailPath && detailPath === normalizeRouteKey(key)
        })
      : null
    if (detailPathRoute) {
      currentRoute = detailPathRoute
    }
    if (!currentRoute) {
      Notification.error({
        title: locale['t_error_title'],
        content: locale['t_route_error'],
        showIcon: true,
        position: 'bottomRight',
        duration: 1500,
      })
      return
    }
    if (currentRoute.detailPath && extra?.action) {
      const action = extra.action
      const id = extra.id || ''
      const isDetailPathRoute =
        normalizeRouteKey(routeKey) ===
        normalizeRouteKey(currentRoute.detailPath || currentRoute.detailFormRoute)
      key = isDetailPathRoute
        ? `${routeKey}/${id || 'create'}`
        : `${routeKey}/${action}${id ? `/${id}` : ''}`
      const findRoute = flattenRoutes.find((r) => r.key === key)
      if (findRoute) {
        currentRoute = findRoute
        if (isDetailPathRoute && currentRoute.dynamicDetail) {
          currentRoute = {
            ...currentRoute,
            dynamicDetail: {
              ...currentRoute.dynamicDetail,
              action,
              actionTitle: getActionTitle(
                action,
                currentRoute.dynamicDetail.parentRoute.detailActionTitles
              ),
            },
          }
        }
      } else {
        let detailRoute = matchDynamicDetailRoute(`/${key}`, flattenRoutes)
        if (isDetailPathRoute && detailRoute) {
          detailRoute = {
            ...detailRoute,
            action,
            actionTitle: getActionTitle(
              action,
              detailRoute.parentRoute.detailActionTitles
            ),
          }
        }
        currentRoute = {
          ...currentRoute,
          key,
          name: detailRoute.detailTitle.name,
          nameEn: detailRoute.detailTitle.nameEn,
          routeKey: detailRoute.parentKey,
          dynamic: false,
          dynamicDetail: detailRoute,
          component: Loadable({
            loader: () => import(`./pages/${detailRoute.parentRoute.detailPath}`),
            loading: Loading,
          }),
        }
        flattenRoutes.push(currentRoute)
      }
      const cacheData = buildDynamicDetailCacheRoute(
        currentRoute.dynamicDetail,
        lang
      )
      subTitle = extra.subTitle ?? cacheData.subTitle
      customTitle = extra.customTitle ?? cacheData.customTitle
      urlParams = extra?.urlParams || ''
      if (urlParams) urlParams = '?' + urlParams
    } else if (extra === undefined) {
      extra = { hasNotParam: true }
    }
    if (currentRoute.dynamic && extra) {
      extra['dynamic'] = true
      subTitle = extra['subTitle']
      customTitle = extra?.['customTitle']
      urlParams = extra?.['urlParams']
      if (!urlParams) urlParams = ''
      if (urlParams) urlParams = '?' + urlParams
      const id = extra['id']
      key = routeKey + (id ? '/' + id : '/create')
      const findRoute = flattenRoutes.find((r) => r.key === key)
      if (!findRoute) {
        currentRoute = {
          ...currentRoute,
          key,
          routeKey,
          dynamic: false,
        }
        currentRoute.component = Loadable({
          loader: () => import(`./pages/${currentRoute.path ?? routeKey}`),
          loading: Loading,
        })
        flattenRoutes.push(currentRoute)
      } else {
        currentRoute = findRoute
      }
    }
    const preload = currentRoute.component.preload()
    NProgress.start()
    preload.then((component) => {
      dispatch(
        addCacheRoute({
          cacheKey: key,
          path: '/' + key,
          name: currentRoute.name,
          nameEn: currentRoute.nameEn,
          subTitle: subTitle,
          customTitle: customTitle,
          urlParams: urlParams,
        })
      )
      history.push(`/${key}${urlParams}`)
      NProgress.done()
      if (currentRoute.dynamicDetail) {
        dispatch(
          trigger({
            name: 'dynamicDetail.init',
            params:
              currentRoute.detailFormRoute ||
              currentRoute.dynamicDetail.parentRoute.detailFormRoute ||
              currentRoute.dynamicDetail.parentRoute.detailPath,
            data: {
              ...extra,
              id: currentRoute.dynamicDetail.id,
              action: currentRoute.dynamicDetail.action,
              cacheKey: key,
              formData: extra?.formData ?? null,
              disabled: extra?.disabled ?? false,
            },
          })
        )
        return
      }
      if (extra['dynamic'] && extra['hasNotParam']) {
        dispatch(
          trigger({
            name: 'history.push',
            params: routeKey,
            data: {
              formData: null,
              id: '',
              event: null,
              disabled: false,
              customTitle: customTitle,
            },
          })
        )
      }
    })
  }

  function toggleCollapse() {
    dispatch(setMenuCollapse(!menuCollapsed))
  }

  const paddingLeft = showMenu ? { paddingLeft: menuWidth } : {}
  const paddingTop = showNavbar ? { paddingTop: navbarHeight } : {}
  const paddingStyle = { ...paddingLeft, ...paddingTop }

  function updateMenuStatus() {
    const pathKeys = pathname.split('/')
    const newSelectedKeys: string[] = []
    const newOpenKeys: string[] = [...openKeys]
    while (pathKeys.length > 0) {
      const currentRouteKey = pathKeys.join('/')
      const menuKey = currentRouteKey.replace(/^\//, '')
      const menuType = menuMap.current.get(menuKey)
      if (menuType && menuType.menuItem) {
        newSelectedKeys.push(menuKey)
      }
      if (menuType && menuType.subMenu && !openKeys.includes(menuKey)) {
        newOpenKeys.push(menuKey)
      }
      pathKeys.pop()
    }
    setSelectedKeys(newSelectedKeys)
    setOpenKeys(newOpenKeys)
  }
  useEffect(() => {
    if (event) {
      if (event.name == 'history.push' && event.params) {
        const key = event?.params
        const id = event?.data?.id
        const subTitle = event?.data?.title
        const customTitle = event?.data?.customTitle
        const urlParams = event?.data?.urlParams
        onClickMenuItem(
          key,
          event.data
            ? {
                ...event.data,
                id,
                subTitle,
                customTitle,
                urlParams,
              }
            : undefined
        )
      }
    }
  }, [event])
  // eslint-disable-next-line
  useEffect(() => {
    let routeConfig = routeMap.current.get(pathname)
    if (!routeConfig) {
      const findObj = flattenRoutes.find((c) => '/' + c.key == pathname)
      if (findObj) {
        routeConfig = routeMap.current.get('/' + findObj.routeKey)
        if (findObj.dynamicDetail) {
          routeConfig = getDynamicDetailBreadcrumb(
            routeConfig || [],
            findObj.dynamicDetail
          )
        }
        // if (routeConfig?.length == 1) {
        //   //当前的路由没有上级？24-1-15
        //   const route = pathname.split('/');
        //   const result = route.slice(1, route.length - 2).join('/');
        //   const resRouteConfig = [];
        //   routeMap.current.forEach((value, key) => {
        //     if (key.includes(result) && value.length > 1) {
        //       const resultLen = result.split('/').length;
        //       for (let index = 0; index < resultLen - 1; index++) {
        //         resRouteConfig.push(value[index]);
        //       }
        //     }
        //   });
        //   resRouteConfig.push(routeMap.current.get('/' + findObj.routeKey));
        //   routeConfig = resRouteConfig;
        // }
      }
    }
    if (!routeConfig) {
      const dynamicDetailRoute = matchDynamicDetailRoute(pathname, flattenRoutes)
      if (dynamicDetailRoute) {
        routeConfig = getDynamicDetailBreadcrumb(
          routeMap.current.get('/' + dynamicDetailRoute.parentKey) || [],
          dynamicDetailRoute
        )
      }
    }

    setBreadCrumb(routeConfig || [])
    updateMenuStatus()
  }, [pathname, routes, lang])

  // function travel(_routes: IRoute[], level, parentNode = []) {
  //   const popupMenu = popUpMenu
  //   return _routes.map((route) => {
  //     const { breadcrumb = true, ignore } = route
  //     const titleDom = getMenuTitle(route)
  //     const setRouteMap = (route, parentNode = []) => {
  //       const title = !lang || lang == 'zh-CN' ? route.name : route.nameEn
  //       routeMap.current.set(
  //         `/${route.key}`,
  //         breadcrumb ? [...parentNode, title] : []
  //       )
  //     }
  //     setRouteMap(route, parentNode)
  //     const visibleChildren = (route.children || []).filter((child) => {
  //       const { ignore, breadcrumb = true } = child
  //       if (ignore || route.ignore) {
  //         // setRouteMap(child, parentNode);
  //         const title = !lang || lang == 'zh-CN' ? route.name : route.nameEn
  //         setRouteMap(
  //           child,
  //           parentNode?.length ? [...parentNode, title] : [route.name]
  //         ) //新增手动设置parent 24-4-11[...parentNode, title]多传一层
  //       }
  //       return !ignore
  //     })
  //     if (
  //       route.children &&
  //       route.children?.every((child) => child.ignore === true)
  //     ) {
  //       return ''
  //     }
  //     if (ignore) {
  //       return ''
  //     }

  //     if (visibleChildren.length) {
  //       const title = !lang || lang == 'zh-CN' ? route.name : route.nameEn
  //       menuMap.current.set(route.key, { subMenu: true })
  //       return popupMenu ? (
  //         <MenuWithPopover
  //           type={popUpMenuType}
  //           setRouteMap={setRouteMap}
  //           parentNode={[...parentNode, title]}
  //           key={route.key}
  //           route={route}
  //           visibleChildren={visibleChildren}
  //           onClickMenuItem={(routekey) => {
  //             onClickMenuItem(routekey)
  //           }}
  //         />
  //       ) : (
  //         <SubMenu key={route.key} title={titleDom}>
  //           {travel(visibleChildren, level + 1, [...parentNode, title])}
  //         </SubMenu>
  //       )
  //     } else {
  //       // const roles = configuration?.currentUser?.roles;
  //       // if (roles && roles.find((c) => c == 'admin')) {
  //       menuMap.current.set(route.key, { menuItem: true })
  //       return <MenuItem key={route.key}>{titleDom}</MenuItem>
  //       // }
  //     }
  //   })
  // }

  useEffect(() => {
    if (!location?.pathname || flattenRoutes.length <= 0) return

    const pathName = location.pathname
    const search = location.search
    const urlParams = search ? search.substring(1) : ''
    const findObj = flattenRoutes.find((c) => '/' + c.key == pathName)
    if (findObj) {
      const findidx = cacheRoute.findIndex((c) => c.cacheKey === findObj.key)
      if (findidx >= 0) return

      const resLang = lang == 'zh-CN' ? 'name' : 'nameEn'
      if (location.search) {
        const params = parseQueryString()
        if (params?.InnerNo) {
          const title = findObj[resLang]
          const customTitle = `${title}(${params?.InnerNo})`
          dispatch(
            addCacheRoute({
              cacheKey: findObj.key,
              path: '/' + findObj.key,
              name: findObj.name,
              nameEn: findObj.nameEn,
              customTitle,
              urlParams: location.search,
            })
          )
          return
        }
      }

      dispatch(
        addCacheRoute({
          cacheKey: findObj.key,
          path: '/' + findObj.key,
          name: findObj.name,
          nameEn: findObj.nameEn,
          urlParams: location.search,
        })
      )
      return
    }

    const dynamicDetailRoute = matchDynamicDetailRoute(pathName, flattenRoutes)
    if (dynamicDetailRoute) {
      const findRoute = flattenRoutes.find(
        (route) => route.key === dynamicDetailRoute.detailKey
      )
      if (!findRoute) {
        flattenRoutes.push({
          ...dynamicDetailRoute.parentRoute,
          key: dynamicDetailRoute.detailKey,
          name: dynamicDetailRoute.detailTitle.name,
          nameEn: dynamicDetailRoute.detailTitle.nameEn,
          routeKey: dynamicDetailRoute.parentKey,
          dynamic: false,
          dynamicDetail: dynamicDetailRoute,
          component: Loadable({
            loader: () =>
              import(`./pages/${dynamicDetailRoute.parentRoute.detailPath}`),
            loading: Loading,
          }),
        })
      }
      const cacheData = buildDynamicDetailCacheRoute(dynamicDetailRoute, lang)
      dispatch(
        addCacheRoute({
          ...cacheData,
          urlParams: location.search,
        })
      )
      dispatch(
        trigger({
          name: 'dynamicDetail.init',
          params:
            dynamicDetailRoute.parentRoute.detailFormRoute ||
            dynamicDetailRoute.parentRoute.detailPath,
          data: {
            id: dynamicDetailRoute.id,
            action: dynamicDetailRoute.action,
            cacheKey: dynamicDetailRoute.detailKey,
            formData: null,
            disabled: dynamicDetailRoute.action === 'view',
            urlParams,
          },
        })
      )
      return
    }

    const paths = pathName.split('/')
    if (paths.length <= 0) return

    const id = paths[paths.length - 1]
    if (id.length == 36) {
      const params = pathName.substr(1, pathName.length - 38)
      onClickMenuItem(params, { id: id, subTitle: '' })
      dispatch(
        trigger({
          name: 'history.push',
          params: params,
          data: {
            formData: null,
            id: id,
            event: null,
            disabled: true,
            urlParams: urlParams,
          },
        })
      )
    } else if (id == 'create') {
      const params = pathName.substr(1, pathName.length - 8)
      onClickMenuItem(params)
    }
  }, [location.pathname, location.search, flattenRoutes, cacheRoute, lang])

  const scrollToAnchor = (anchorName) => {
    if (anchorName) {
      // 找到锚点
      const anchorElement = document.getElementById(anchorName)
      // 如果对应id的锚点存在，就跳转到锚点
      if (anchorElement) {
        anchorElement.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    }
  }

  return (
    <Layout className={styles.layout}>
      <div
        className={cs(styles['layout-navbar'], {
          // [styles['layout-navbar-hidden']]: !showNavbar,
        })}
      >
        <Navbar show={showNavbar} />
      </div>
      {userLoading ? (
        <Spin className={styles['spin']} />
      ) : (
        <Layout>
          {showMenu && (
            <Sider
              className={styles['layout-sider']}
              width={menuWidth}
              collapsed={menuCollapsed}
              onCollapse={(val) => {
                dispatch(setMenuCollapse(val))
              }}
              trigger={null}
              collapsible
              breakpoint="xl"
              style={{ ...paddingTop }}
            >
              <div className={styles['menu-wrapper']}>
                <div
                  style={{ padding: '6px 6px 6px 6px' }}
                  className="menu-search"
                >
                  <AutoComplete
                    allowClear
                    data={flattenRoutes
                      .filter(
                        (c) =>
                          !c.ignore && (!c.children || c.children.length == 0)
                      )
                      .map((c) => {
                        const title =
                          !lang || lang == 'zh-CN' ? c.name : c.nameEn
                        return {
                          value: title, // locale[c.name],
                          name: title, //locale[c.name],
                          key: c.key,
                        }
                      })}
                    placeholder={locale['t_menu']} //"功能菜单"
                    triggerElement={
                      <Input
                        style={{ textAlign: 'center' }}
                        prefix={
                          <div onClick={() => dispatch(setMenuCollapse(false))}>
                            <IconSearch />
                          </div>
                        }
                      />
                    }
                    // value=""
                    onChange={(value, option) => {
                      if (option?.extra?.key) {
                        onClickMenuItem(option?.extra?.key)
                      }
                    }}
                    filterOption={(value, option) => {
                      return String((option as any)?.value || '').indexOf(value) >= 0
                    }}
                    // onSearch={handleSearch}
                  />
                </div>
                <Menu
                  levelIndent={16}
                  mode={popUpMenu ? 'pop' : undefined}
                  collapse={menuCollapsed}
                  onClickMenuItem={(routekey) => {
                    onClickMenuItem(routekey)
                  }}
                  selectedKeys={selectedKeys}
                  openKeys={openKeys}
                  onClickSubMenu={(_, openKeys) => setOpenKeys(openKeys)}
                  style={{ paddingBottom: 20 }}
                >
                  {renderRoutes(routes, 1)}
                </Menu>
              </div>
              <div className={styles['collapse-btn']} onClick={toggleCollapse}>
                {menuCollapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </div>
            </Sider>
          )}
          <Layout className={styles['layout-content']} style={paddingStyle}>
            <HeaderTab />
            <div className={styles['layout-content-wrapper']}>
              {!!displayBreadcrumb.length &&
                displayBreadcrumb[0] && getBreadcrumbText(displayBreadcrumb[0], locale, lang) != locale['t_workplace'] && (
                  <div className={styles['layout-breadcrumb']}>
                    <Breadcrumb>
                      {displayBreadcrumb.map((node, index) => (
                        <Breadcrumb.Item key={index}>
                          {typeof node === 'object' && node !== null
                            ? ((!lang || lang === 'zh-CN') ? node.name : (node.nameEn || node.name))
                            : (typeof node === 'string'
                              ? locale[node] || node
                              : node)}
                        </Breadcrumb.Item>
                      ))}
                    </Breadcrumb>
                    <div className="layout-breadcrumb-right">
                      <Anchor
                        affix={false}
                        direction="horizontal"
                        lineless
                        hash={false}
                      >
                        {anchorPoint &&
                          anchorPoint
                            .filter(
                              (c) =>
                                (pathname + '/').indexOf('/' + c.group + '/') ==
                                0
                            )
                            .map((item, index) => (
                              <AnchorLink
                                key={index}
                                href={'#' + item.id}
                                title={item.title}
                              />
                            ))}
                      </Anchor>

                      {/* {anchorPoint && anchorPoint.filter(c=> (pathname+"/").indexOf("/"+ c.group+"/") == 0).map((item,index)=><a key={index} onClick={() => scrollToAnchor(item.id)}>{"<"+item.title+">"}</a>)} */}
                    </div>
                  </div>
                )}
              <Content>
                <Switch>
                  {flattenRoutes
                    .filter((c) => !c.noCache && !c.dynamic)
                    .map((route, index) => {
                      return (
                        <Route key={index} exact path={`/${route.key}`} /> // placeholder(required)
                      )
                    })}
                  {flattenRoutes
                    .filter((c) => c.noCache)
                    .map((route, index) => {
                      return (
                        <Route
                          key={index}
                          exact
                          path={`/${route.key}`}
                          render={(props) => {
                            const Component = route.component
                            return <Component {...props} currMenu={route} />
                          }}
                        />
                      )
                    })}
                  <Route exact path="/">
                    <Redirect to={`/${defaultRoute}`} />
                  </Route>
                  {devRouteComponent && (
                    <Route
                      key={`dev-${location.pathname}`}
                      path={location.pathname}
                      component={devRouteComponent}
                    />
                  )}
                  <Route
                    path="*"
                    component={lazyload(() => import('./pages/exception/403'))}
                  />
                </Switch>
                {flattenRoutes
                  .filter((c) => !c.noCache && !c.dynamic)
                  .map((route, index) => {
                    // return (
                    //   <LiveRoute
                    //     key={index}
                    //     path={`/${route.key}`}
                    //     alwaysLive={true}
                    //     component={route.component}
                    //     forceUnmount={() => {
                    //       const isFind = cacheRoute.find(
                    //         (c) => c.cacheKey == route.key
                    //       )
                    //       return !isFind
                    //     }}
                    //   />
                    // )
                    return (
                      <DynamicLiveRoute
                        index={index}
                        cacheRoute={cacheRoute}
                        key={route.key}
                        route={route}
                      />
                    )
                  })}
              </Content>
            </div>
            {showFooter && <Footer />}
          </Layout>
        </Layout>
      )}
    </Layout>
  )
}

export default PageLayout

